import { useTranslation } from "react-i18next";
import { useMemo, useRef, useCallback } from "react";
import { GitBranch } from "lucide-react";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  type Edge,
  type Node,
  type ReactFlowInstance,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import TriggerNode from "./trigger-node";
import StepNode from "./step-node";
import EndNode from "./end-node";
import { NODE_SPACING } from "./types";
import type { ILeaveApprovalSteps } from "@/types/(my-space)/leaves.interface";
import type { IAssetApprovalSteps } from "@/types/(company)/assets.interface";

// ─── Constants ────────────────────────────────────────────────────────────────
const NODE_TYPES = { trigger: TriggerNode, step: StepNode, end: EndNode };

const NODE_WIDTH = 372; // must match w-[372px] on all node components

const EDGE_STYLE = {
  stroke: "var(--color-info-muted)",
  strokeWidth: 1.5,
  strokeDasharray: "5 4",
};

// ─── Props ────────────────────────────────────────────────────────────────────
interface ApprovalStepsFlowProps {
  approvalSteps?: ILeaveApprovalSteps | IAssetApprovalSteps;
  triggerContent?: React.ReactNode;
  showHeader?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────
const ApprovalStepsFlow = ({
  approvalSteps,
  triggerContent,
  showHeader = true,
}: ApprovalStepsFlowProps) => {
  const { t } = useTranslation(["common"]);
  const steps = approvalSteps?.steps ?? [];
  const currentStep = approvalSteps?.currentStep ?? 1;
  const totalSteps = approvalSteps?.totalSteps ?? 0;
  const allApproved = steps.every((s) => s.status === "approved");

  // Container ref to read the actual rendered canvas width on init
  const containerRef = useRef<HTMLDivElement>(null);

  // On ReactFlow init, centre the first node horizontally using the real canvas width.
  // This avoids the hardcoded x-offset being wrong on narrow mobile screens.
  const handleInit = useCallback((rfInstance: ReactFlowInstance) => {
    const canvasWidth = containerRef.current?.clientWidth ?? 440;
    const zoom = canvasWidth < 440 ? 0.75 : 0.9;
    const x = Math.max(8, (canvasWidth - NODE_WIDTH * zoom) / 2);
    rfInstance.setViewport({ x, y: 24, zoom });
  }, []);

  const { nodes, edges } = useMemo(() => {
    const triggerNode: Node = {
      id: "trigger",
      type: "trigger",
      position: { x: 0, y: 0 },
      draggable: false,
      data: { user: approvalSteps?.startingPoint?.requester, triggerContent },
    };

    // Accumulate y-position accounting for variable node heights
    let yOffset = NODE_SPACING; // after trigger

    const stepNodes: Node[] = steps.map((step) => {
      const nodeStatus =
        step.status === "approved"
          ? "approved"
          : step.status === "rejected"
            ? "rejected"
            : step.isCurrent && step.status === "pending"
              ? "current"
              : "pending";

      const node: Node = {
        id: `step-${step.stepOrder}`,
        type: "step",
        position: { x: 0, y: yOffset },
        draggable: false,
        data: {
          stepOrder: step.stepOrder,
          nodeName: step.stepName,
          positionName: step.positionName,
          approver: step.approver,
          type: step.type ?? null,
          approvers: step.approvers ?? null,
          branch: step.approver?.branch ?? step.branch ?? null,
          actionAt: step.actionAt ?? null,
          comments: step.comments ?? null,
          status: nodeStatus,
        },
      };

      yOffset += NODE_SPACING;

      return node;
    });

    const approvedCount = steps.filter((s) => s.status === "approved").length;

    const endNode: Node = {
      id: "end",
      type: "end",
      position: { x: 0, y: yOffset },
      draggable: false,
      data: {
        allApproved,
        instanceStatus: approvalSteps?.instanceStatus,
        approvedCount,
        totalSteps: steps.length,
      },
    };

    const allNodes = [triggerNode, ...stepNodes, endNode];

    const edges: Edge[] = allNodes.slice(0, -1).map((node, index) => ({
      id: `e-${node.id}-${allNodes[index + 1].id}`,
      source: node.id,
      target: allNodes[index + 1].id,
      style: EDGE_STYLE,
    }));

    return { nodes: allNodes, edges };
  }, [approvalSteps, triggerContent]);

  if (!approvalSteps) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 py-16 px-6 text-center">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-100">
          <GitBranch className="w-5 h-5 text-gray-400" />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium text-gray-600">
            {t("common:workflow.no_approval_steps")}
          </p>
          <p className="text-xs text-gray-400 leading-relaxed">
            {t("common:workflow.request_has_no_workflow")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {showHeader && (
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0">
          <h4 className="text-sm font-semibold text-gray-900">
            {t("common:workflow.approval_steps")}
          </h4>
          <span className="text-sm text-gray-500">
            <span className="text-gray-900 font-bold">
              {String(currentStep).padStart(2, "0")}
            </span>
            {" / "}
            {String(totalSteps).padStart(2, "0")}
          </span>
        </div>
      )}

      <div ref={containerRef} className="flex-1 h-full bg-muted">
        <ReactFlowProvider>
          <ReactFlow
            nodeTypes={NODE_TYPES}
            nodes={nodes}
            edges={edges}
            onInit={handleInit}
            minZoom={0.2}
            maxZoom={1.5}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            panOnDrag
            zoomOnScroll
            proOptions={{ hideAttribution: true }}
          >
            <Background variant={BackgroundVariant.Dots} />
          </ReactFlow>
        </ReactFlowProvider>
      </div>
    </div>
  );
};

export default ApprovalStepsFlow;
